import { useEffect, useState } from "react";
import { Link, useNavigate } from "react-router-dom";
import api, { formatApiError } from "@/lib/api";
import { useAuth } from "@/contexts/AuthContext";
import { formatDate } from "@/lib/format";
import AdminNav from "@/components/AdminNav";
import "@/styles/portfolio.css";
import "@/styles/admin.css";

export default function AdminDashboard() {
  const { user, logout } = useAuth();
  const navigate = useNavigate();
  const [posts, setPosts] = useState([]);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState("");
  const [busyId, setBusyId] = useState(null);

  const load = async () => {
    setLoading(true);
    try {
      const { data } = await api.get("/admin/posts");
      setPosts(data);
      setError("");
    } catch (e) {
      setError(formatApiError(e));
    } finally {
      setLoading(false);
    }
  };

  useEffect(() => {
    load();
  }, []);

  const handleDelete = async (id, title) => {
    if (!window.confirm(`Beitrag „${title}" wirklich löschen?`)) return;
    setBusyId(id);
    try {
      await api.delete(`/admin/posts/${id}`);
      setPosts((p) => p.filter((x) => x.id !== id));
    } catch (e) {
      setError(formatApiError(e));
    } finally {
      setBusyId(null);
    }
  };

  const handleLogout = async () => {
    await logout();
    navigate("/admin/login", { replace: true });
  };

  return (
    <div className="admin-shell" data-testid="admin-dashboard">
      <header className="admin-header">
        <div>
          <span className="eyebrow">ADMIN · BLOG</span>
          <h1 className="display admin-h1">
            Deine <span className="italic">Beiträge</span>.
          </h1>
          {user && (
            <p className="admin-meta">
              Angemeldet als <strong>{user.email}</strong>
            </p>
          )}
        </div>
        <div className="admin-header__actions">
          <Link to="/admin/new" className="btn btn--solid" data-testid="admin-new-post">
            <span>Neuer Beitrag</span>
            <span className="btn__arrow">→</span>
          </Link>
          <button
            type="button"
            onClick={handleLogout}
            className="btn btn--ghost"
            data-testid="admin-logout"
          >
            <span>Abmelden</span>
          </button>
        </div>
      </header>

      {error && <div className="admin-error">{error}</div>}

      <AdminNav active="/admin" />

      <section className="admin-posts">
        {loading && <p className="admin-empty">Lädt…</p>}
        {!loading && posts.length === 0 && (
          <p className="admin-empty">
            Noch keine Beiträge. Lege deinen ersten an.
          </p>
        )}
        <ul className="admin-post-list" data-testid="admin-post-list">
          {posts.map((p) => (
            <li
              key={p.id}
              className="admin-post-row"
              data-testid={`admin-post-${p.slug}`}
            >
              <div className="admin-post-row__main">
                <span className="admin-post-row__date">
                  {formatDate(p.created_at)}
                </span>
                <h2 className="admin-post-row__title">
                  {p.title}
                  {!p.published && <em className="admin-draft"> · Entwurf</em>}
                </h2>
                <p className="admin-post-row__excerpt">{p.excerpt}</p>
              </div>
              <div className="admin-post-row__actions">
                <Link
                  to={`/blog/${p.slug}`}
                  target="_blank"
                  rel="noopener noreferrer"
                  className="admin-link"
                >
                  Vorschau
                </Link>
                <Link to={`/admin/edit/${p.id}`} className="admin-link admin-link--accent">
                  Bearbeiten
                </Link>
                <button
                  type="button"
                  className="admin-link admin-link--danger"
                  disabled={busyId === p.id}
                  onClick={() => handleDelete(p.id, p.title)}
                  data-testid={`admin-delete-${p.slug}`}
                >
                  {busyId === p.id ? "Löscht…" : "Löschen"}
                </button>
              </div>
            </li>
          ))}
        </ul>
      </section>
    </div>
  );
}
